import {
  Box,
  HStack,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import {
  useEffect,
  useMemo,
  type FC,
  type ForwardedRef,
  type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';

import { FilterTag } from '$/components/core/Collection/MaterialPresentation/DataSortingRow/components/FilterTag';
import { GenericTagContent } from '$/components/core/Collection/MaterialPresentation/DataSortingRow/components/GenericTagContent';
import { ShowAdditionalFilterTagsButton } from '$/components/core/Collection/MaterialPresentation/DataSortingRow/components/ShowAdditionalFilterTagsButton';
import { usePortalContainer } from '$/contexts/PortalContainerContext';
import { useAppliedFilterOptions } from '$/hooks/data/useAppliedFilterOptions';
import { useCollapsibleList } from '$/hooks/useCollapsibleList';
import { useCollectionStore } from '$/stores/useCollectionStore';

interface Props {
  scrollRefs?: (RefObject<HTMLDivElement> | ForwardedRef<HTMLDivElement>)[];
}

export const FilterTags: FC<Props> = ({ scrollRefs = [] }) => {
  const { t } = useTranslation();
  const portalContainerRef = usePortalContainer();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const appliedFilterOptions = useAppliedFilterOptions();
  const brightnessFilter = useCollectionStore.useBrightnessFilter();

  const {
    invisibleItemCount,
    parentRef,
    getCollectorProps,
    getChildrenProps,
    visibleItemCount,
  } = useCollapsibleList(appliedFilterOptions, brightnessFilter);

  const filterTags = useMemo(() => {
    if (!appliedFilterOptions) return undefined;

    const labels: string[] = appliedFilterOptions.map((filterOption) => {
      return filterOption.items
        .map(({ displayName }) => displayName)
        .join(', ');
    });

    const tags = appliedFilterOptions.map((filterOption) => {
      return (
        <FilterTag
          key={filterOption.searchField}
          onDelete={() =>
            useCollectionStore
              .getState()
              .resetAppliedFilterOption([filterOption.searchField])
          }
        >
          {filterOption.items.map(({ displayName, payload }) => (
            <GenericTagContent
              key={displayName}
              label={displayName}
              color={payload}
              hideLabel={
                filterOption.searchField === 'colorRanges' &&
                filterOption.items.length > 3
              }
            />
          ))}
        </FilterTag>
      );
    });

    if (brightnessFilter[0] !== 0 || brightnessFilter[1] !== 100) {
      const brightnessLabel = t('dashboard.collection.brightnessChip', {
        from: brightnessFilter[0],
        to: brightnessFilter[1],
      });
      labels.push(brightnessLabel);
      tags.push(
        <FilterTag
          onDelete={() =>
            useCollectionStore
              .getState()
              .resetAppliedFilterOption(['brightness'])
          }
        >
          <GenericTagContent label={brightnessLabel} />
        </FilterTag>,
      );
    }

    return { labels, tags };
  }, [appliedFilterOptions, brightnessFilter, t]);

  useEffect(() => {
    const handleScroll = () => isOpen && onClose();

    const cleanup = scrollRefs.map((ref) => {
      const current = (ref as React.RefObject<HTMLDivElement>).current;
      current?.addEventListener('scroll', handleScroll, { passive: true });
      return () => current?.removeEventListener('scroll', handleScroll);
    });

    return () => cleanup.forEach((fn) => fn());
  }, [scrollRefs, isOpen, onClose]);

  return (
    <Popover
      closeOnBlur={true}
      isOpen={isOpen}
      onClose={onClose}
      onOpen={onOpen}
      placement='bottom-start'
    >
      <HStack ref={parentRef} gap='2' overflowX='hidden'>
        {filterTags && (
          <>
            {filterTags.tags.map((tag, index) => (
              <Box key={tag.key} {...getChildrenProps(index)}>
                {tag}
              </Box>
            ))}

            <Box {...getCollectorProps()}>
              {invisibleItemCount > 0 && (
                <PopoverTrigger>
                  <ShowAdditionalFilterTagsButton>
                    <GenericTagContent
                      label={`+${invisibleItemCount} Filter`}
                      title={filterTags.labels
                        .slice(filterTags.tags.length - invisibleItemCount)
                        .join(', ')}
                    />
                  </ShowAdditionalFilterTagsButton>
                </PopoverTrigger>
              )}
            </Box>
          </>
        )}
      </HStack>

      <Portal containerRef={portalContainerRef}>
        <PopoverContent
          // the use of sx is necessary because of the !important, which is necessary because chakra overwrites the visibility by themselves
          sx={{
            visibility:
              invisibleItemCount === 0 ? 'hidden !important' : 'visible',
          }}
          w='auto'
          p='4'
          bg='background'
          border='1px solid'
          borderColor='border'
          outline='none'
        >
          <Stack flexWrap='wrap'>
            {filterTags?.tags.slice(visibleItemCount).map((tag) => (
              <Box key={tag.key}>{tag}</Box>
            ))}
          </Stack>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
